//===----------------------------------------------------------------------===//
//
//  Copyright 2024-2026 Trovares Inc. dba Rocketgraph.  All rights reserved.
//
//===----------------------------------------------------------------------===//

import { useLayoutEffect } from 'react';

import ChatPanelBody from './ChatPanelBody';
import {
  CHAT_PANEL_ID, CHAT_PANEL_TITLE, chatPanelIcon
} from './chatPanelId';
import { type PanelSpec } from '@/contexts/PanelContext';
import { usePanels } from '@/hooks/usePanels';

// Chat cannot be closed: it is a first-class part of the application rather
// than something an administrator turned on, so iconify is its only
// dismissal and its transcript is never thrown away by accident.
const CHAT_PANEL_SPEC: PanelSpec = {
  id: CHAT_PANEL_ID,
  title: CHAT_PANEL_TITLE,
  icon: chatPanelIcon(),
  size: { w: 500, h: 400 },
  // Taller than the shared floor: the chat body's four rows need the room.
  minSize: { w: 280, h: 300 },
  anchor: 'bottom-right',
  isResizable: true,
  isClosable: false,
  render: () => <ChatPanelBody />
};

/**
 * Registers the chat panel.  Rendered beside the host rather than in the
 * header, because the header remounts on every route change and a registrar
 * that came and went with it would take the panel down too.
 *
 * Reopening it after a reload is the registry's business, not this
 * component's: it holds the remembered set.
 */
const ChatPanelClient = () => {
  const { registerPanel } = usePanels();

  // Before paint, not after: the launcher draws this spec's icon, and a
  // passive effect would register it one painted frame too late, so the
  // header would show the pill without its icon and then grow one.
  useLayoutEffect(() => {
    registerPanel(CHAT_PANEL_SPEC);
  }, [registerPanel]);

  return null;
};

export default ChatPanelClient;
